import {
  CHARACTER_WHITESPACE,
  EMPTY_STRING,
  NOTIFICATION_TYPE,
  SYMBOL_HYPHEN,
} from '../../../../../../constants/common'
import { RENTAL_PORTAL_FACT_SEPARATOR } from '../../../../../../constants/rentalPortal'
import { getMatchCriterionLabelKey } from '../../../../RentalPortalMatch'

import {
  MATCH_RULES_BUDGET_CRITERION,
  MATCH_RULES_CRITERION_UNIT_KEY,
  MATCH_RULES_FIELD_NAME_BUDGET_GATE,
  MATCH_RULES_FIELD_NAME_CRITERIA,
  MATCH_RULES_FIELD_NAME_GATING,
  MATCH_RULES_FIELD_NAME_WEIGHTS,
  MATCH_RULES_GATING_CHANGE,
  MATCH_RULES_GATING_FIELD,
  MATCH_RULES_PARAM,
  MATCH_RULES_PARAM_OPEN,
  MATCH_RULES_PETS_POLICY_LABEL_KEY,
} from './ListingProfileApplicantsMatchRules.constants'

function getIsMatchRulesPetsPolicyField(field) {
  return field === MATCH_RULES_GATING_FIELD.petsPolicy
}

function getIsMatchRulesWbsRequiredField(field) {
  return field === MATCH_RULES_GATING_FIELD.wbsRequired
}

function getMatchRulesGatingText({ field, t, value }) {
  if (getIsMatchRulesPetsPolicyField(field)) {
    return t(MATCH_RULES_PETS_POLICY_LABEL_KEY[value])
  }

  if (getIsMatchRulesWbsRequiredField(field)) {
    return t(
      value
        ? 'RentalPortal.MatchRulesWbsRequiredKey'
        : 'RentalPortal.MatchRulesWbsNotRequiredKey'
    )
  }

  return value || SYMBOL_HYPHEN
}

function getMatchRulesGatingLabel({ field, t }) {
  const { criterion, qualifierKey } = MATCH_RULES_GATING_CHANGE[field]

  return [
    t(getMatchCriterionLabelKey(criterion)),
    qualifierKey && t(qualifierKey),
  ]
    .filter(Boolean)
    .join(CHARACTER_WHITESPACE)
}

function getMatchRulesChangeLine({ from, label, t, to }) {
  return t('RentalPortal.MatchRulesChangeValueKey', { from, label, to })
}

function getMatchRulesCriteriaChanges({ initialValues, t, values }) {
  const initialCriteria = initialValues[MATCH_RULES_FIELD_NAME_CRITERIA]
  const criteria = values[MATCH_RULES_FIELD_NAME_CRITERIA]

  return [
    ...criteria
      .filter(key => !initialCriteria.includes(key))
      .map(key =>
        t('RentalPortal.MatchRulesChangeAddedKey', {
          criterion: t(getMatchCriterionLabelKey(key)),
        })
      ),
    ...initialCriteria
      .filter(key => !criteria.includes(key))
      .map(key =>
        t('RentalPortal.MatchRulesChangeRemovedKey', {
          criterion: t(getMatchCriterionLabelKey(key)),
        })
      ),
  ]
}

function getMatchRulesWeightChanges({ initialValues, t, values }) {
  const initialWeights = initialValues[MATCH_RULES_FIELD_NAME_WEIGHTS]
  const weights = values[MATCH_RULES_FIELD_NAME_WEIGHTS]

  return Object.keys(weights)
    .filter(key => String(weights[key]) !== String(initialWeights[key]))
    .map(key =>
      getMatchRulesChangeLine({
        from: initialWeights[key],
        label: t(getMatchCriterionLabelKey(key)),
        t,
        to: weights[key],
      })
    )
}

function getMatchRulesGatingChanges({ initialValues, t, values }) {
  const initialGating = initialValues[MATCH_RULES_FIELD_NAME_GATING]
  const gating = values[MATCH_RULES_FIELD_NAME_GATING]

  return Object.keys(MATCH_RULES_GATING_CHANGE)
    .filter(
      field =>
        String(gating[field] ?? EMPTY_STRING) !==
        String(initialGating[field] ?? EMPTY_STRING)
    )
    .map(field =>
      getMatchRulesChangeLine({
        from: getMatchRulesGatingText({
          field,
          t,
          value: initialGating[field],
        }),
        label: getMatchRulesGatingLabel({ field, t }),
        t,
        to: getMatchRulesGatingText({ field, t, value: gating[field] }),
      })
    )
}

function getMatchRulesBudgetGateChanges({ initialValues, t, values }) {
  return values[MATCH_RULES_FIELD_NAME_BUDGET_GATE] ===
    initialValues[MATCH_RULES_FIELD_NAME_BUDGET_GATE]
    ? []
    : [
        t(
          values[MATCH_RULES_FIELD_NAME_BUDGET_GATE]
            ? 'RentalPortal.MatchRulesChangeBudgetGatesKey'
            : 'RentalPortal.MatchRulesChangeBudgetScoredKey'
        ),
      ]
}

export function getMatchRulesTooltip({ rules, t }) {
  return [
    t('RentalPortal.MatchRulesBtnKey'),
    rules.summary_line,
    getMatchRulesDistributionTooltip({
      distribution: rules.current_distribution,
      t,
    }),
  ].join(RENTAL_PORTAL_FACT_SEPARATOR)
}

export function getMatchRulesDistributionTooltip({ distribution, t }) {
  return t('RentalPortal.MatchRulesDistributionTooltipKey', {
    eligible: distribution.eligible,
    incomplete: distribution.incomplete,
    notEligible: distribution.not_eligible,
  })
}

export function getMatchRulesGatingCriteria(rules) {
  return rules.criteria.filter(criterion => criterion.gates_eligibility)
}

export function getMatchRulesScoredCriteria(rules) {
  return rules.criteria.filter(criterion => !criterion.gates_eligibility)
}

export function getMatchRulesCriterionLabel({ criterion, t }) {
  const unitKey = MATCH_RULES_CRITERION_UNIT_KEY[criterion.key]

  const label = t(getMatchCriterionLabelKey(criterion.key))

  return unitKey ? `${label} (${t(unitKey)})` : label
}

export function getMatchRulesChangeText({ initialValues, t, values }) {
  const changes = [
    ...getMatchRulesCriteriaChanges({ initialValues, t, values }),
    ...getMatchRulesWeightChanges({ initialValues, t, values }),
    ...getMatchRulesGatingChanges({ initialValues, t, values }),
    ...getMatchRulesBudgetGateChanges({ initialValues, t, values }),
  ]

  return changes.length > 0
    ? t('RentalPortal.MatchRulesChangesKey', {
        changes: changes.join(RENTAL_PORTAL_FACT_SEPARATOR),
      })
    : t('RentalPortal.MatchRulesNoChangesKey')
}

export function getMatchRulesNotifications({
  impact,
  initialValues,
  t,
  values,
}) {
  return [
    t('RentalPortal.MatchRulesWeightsNoteKey'),
    impact,
    getMatchRulesChangeText({ initialValues, t, values }),
  ]
    .filter(Boolean)
    .map(text => ({ text, type: NOTIFICATION_TYPE.info }))
}

export function getMatchRulesWeightHint({ criterion, rules, t }) {
  return criterion.weight_pct
    ? t('RentalPortal.MatchRulesWeightShareKey', {
        share: criterion.weight_pct,
        total: rules.weights_total,
      })
    : t('RentalPortal.MatchRulesWeightOfTotalKey', {
        total: rules.weights_total,
      })
}

export function getIsMatchRulesOpen(searchParams) {
  return searchParams.get(MATCH_RULES_PARAM) === MATCH_RULES_PARAM_OPEN
}

export function getIsMatchRulesBudgetCriterion(criterion) {
  return criterion.key === MATCH_RULES_BUDGET_CRITERION
}
